import type { TFunction } from 'i18next';
import type { AssessmentResultItemDataFragment } from '../../../Trainee/TraineeReports.api';
import { DefaultScores } from '../../constants';
import type {
    ChartSeries,
    PsychologicalRadarGroup,
    PsychologicalResult,
    RadarDataLeadership,
    RadarDatum,
} from '../../model';
import { individualMotivationHeight, ml360Height, selfDeterminationHeight, teamResilienceHeight } from '.';
import { convertShortLabel } from '@/components/blocks/AssessmentResults/shared/utils';
import type { IndividualMotivationAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/IndividualMotivation/IndividualMotivation.api';
import type { SelfDeterminationAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/SelfDetermintation/SelfDetermination.api';
import type { TeamResilienceAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/TeamResilience/TeamResilience.api';
import { DefaultTeamResilienceQuestionCategory } from '@/pages/AssessmentManagementPage/shared/ConfigurationSchema/teamResilienceFormSchema';
import { ml360SeriesColors } from '@/pages/Reports/shared/chartColors';

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/**
 * Right now all these placeholders are very hardcoded because we do not
 * have the data to build them dynamically.
 * Eventually, we will need to use the configurations to build them dynamically.
 */

export const buildSelfDeterminationPlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): PsychologicalResult | null => {
    const mi = item.moduleInstance;

    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    const result = item.result as SelfDeterminationAssessmentResultDataFragment | undefined;
    const categoriesFromResult =
        result?.categoryAverages?.length && result.categoryAverages.every(ca => ca.category)
            ? Array.from(new Set(result.categoryAverages.map(ca => ca.category)))
            : result?.surveys?.length && result.surveys.every(s => s.category)
              ? Array.from(new Set(result.surveys.map(s => s.category)))
              : [];

    // Fallback to translated defaults when the API doesn't provide structure (or it's empty).
    const categories =
        categoriesFromResult.length > 0
            ? categoriesFromResult
            : [
                  t('traineeReports:selfDetermination.autonomy'),
                  t('traineeReports:selfDetermination.competence'),
                  t('traineeReports:selfDetermination.relatedness'),
              ];

    const maxDomain =
        result?.categoryAverages?.length && result.categoryAverages.some(ca => (ca.maximumPossibleScore ?? 0) > 0)
            ? Math.max(1, ...result.categoryAverages.map(ca => ca.maximumPossibleScore ?? 0))
            : DefaultScores.SelfDeterminationMaxScore;

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi?.name ?? '',
        description: mi?.description ?? '',
        radarGroups: [
            {
                key: 'self-determination',
                scoreCards: categories.map(label => ({ label, value: t('traineeReports:na') })),
                charts: [
                    {
                        data: categories.map(axis => ({ axis, individual: 0 })),
                        maxDomain,
                        height: selfDeterminationHeight,
                        variant: 'bare',
                        footer: t('traineeReports:labels.individual'),
                    },
                ],
            },
        ],
    };
};

export const buildTeamResiliencePlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): PsychologicalResult | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    const result = item.result as TeamResilienceAssessmentResultDataFragment | undefined;
    const teamResourcesSubcategoriesFromResult = result?.subcategoryAverages?.length
        ? Array.from(
              new Set(
                  result.subcategoryAverages
                      .filter(sa => sa.category === DefaultTeamResilienceQuestionCategory.TeamResources)
                      .map(sa => sa.subcategory)
                      .filter(isNonEmptyString)
              )
          )
        : result?.surveys?.length
          ? Array.from(
                new Set(
                    result.surveys
                        .filter(s => s.category === DefaultTeamResilienceQuestionCategory.TeamResources)
                        .map(s => s.subcategory)
                        .filter(isNonEmptyString)
                )
            )
          : [];

    // Fallback to translated defaults when the API doesn't provide structure (or it's empty).
    const axes =
        teamResourcesSubcategoriesFromResult.length > 0
            ? teamResourcesSubcategoriesFromResult
            : [
                  t('traineeReports:teamResilience.physical'),
                  t('traineeReports:teamResilience.affective'),
                  t('traineeReports:teamResilience.cognitive'),
              ];

    const individualRadarData: RadarDatum[] = axes.map(axis => ({ axis, individual: 0 }));
    const teamRadarData: RadarDatum[] = axes.map(axis => ({ axis, individual: 0, team: 0 }));
    const maxDomain =
        result?.subcategoryAverages?.length && result.subcategoryAverages.some(sa => (sa.maximumPossibleScore ?? 0) > 0)
            ? Math.max(1, ...result.subcategoryAverages.map(sa => sa.maximumPossibleScore ?? 0))
            : DefaultScores.TeamResilienceMaxScore;

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi.name ?? '',
        description: mi.description ?? '',
        radarGroups: [
            {
                key: 'team-resilience',
                scoreCards: axes.map(label => ({ label, value: t('traineeReports:na') })),
                charts: [
                    {
                        data: individualRadarData,
                        maxDomain,
                        height: teamResilienceHeight,
                        variant: 'bare',
                        footer: t('traineeReports:labels.individual'),
                        showTeam: false,
                    },
                    {
                        data: teamRadarData,
                        maxDomain,
                        height: teamResilienceHeight,
                        variant: 'bare',
                        footer: t('traineeReports:labels.team'),
                        showTeam: true,
                    },
                ],
            },
        ],
    };
};

export const buildMl360Placeholder = (
    t: TFunction,
    items: AssessmentResultItemDataFragment[]
): PsychologicalResult | null => {
    // Any of the ml360 module instances can represent the section (used for iconKey / title)
    const base =
        items.find(i => i.moduleInstance?.moduleDefinitionIdentifier === 'ml360-self') ??
        items.find(i => i.moduleInstance?.moduleDefinitionIdentifier === 'ml360-buddy') ??
        items.find(i => i.moduleInstance?.moduleDefinitionIdentifier === 'ml360-trainer');

    const mi = base?.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    const rawAxes = [
        t('traineeReports:ml360.awareness'),
        t('traineeReports:ml360.calmAndPatience'),
        t('traineeReports:ml360.openMind'),
        t('traineeReports:ml360.focusAndPresence'),
        t('traineeReports:ml360.growth'),
    ];
    const axes = rawAxes.map(convertShortLabel);

    const radarData: RadarDataLeadership[] = axes.map(axis => ({
        axis,
        self: 0,
        buddy: 0,
        trainer: 0,
    }));

    const series: ChartSeries[] = [
        {
            key: 'self',
            name: t('traineeReports:labels.self'),
            stroke: ml360SeriesColors.self.stroke,
            fill: ml360SeriesColors.self.fill,
            fillOpacity: ml360SeriesColors.self.fillOpacity,
        },
        {
            key: 'buddy',
            name: t('traineeReports:labels.buddy'),
            stroke: ml360SeriesColors.buddy.stroke,
            fill: ml360SeriesColors.buddy.fill,
            fillOpacity: ml360SeriesColors.buddy.fillOpacity,
        },
        {
            key: 'trainer',
            name: t('traineeReports:labels.trainer'),
            stroke: ml360SeriesColors.trainer.stroke,
            fill: ml360SeriesColors.trainer.fill,
            fillOpacity: ml360SeriesColors.trainer.fillOpacity,
        },
    ];

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi.name ?? t('traineeReports:ml360.title'),
        description: mi.description ?? t('traineeReports:ml360.description'),
        radarGroups: [
            {
                key: 'ml360',
                scoreCards: axes.map(label => ({ label, value: t('traineeReports:na') })),
                charts: [
                    {
                        data: radarData,
                        series,
                        maxDomain: DefaultScores.Ml360MaxScore,
                        height: ml360Height,
                        variant: 'bare',
                        footer: t('traineeReports:labels.individual'),
                    },
                ],
            },
        ],
    };
};

export const buildIndividualMotivationPlaceholder = (
    t: TFunction,
    item: AssessmentResultItemDataFragment
): PsychologicalResult | null => {
    const mi = item.moduleInstance;
    if (!mi?.moduleDefinitionIdentifier) {
        return null;
    }

    const result = item.result as IndividualMotivationAssessmentResultDataFragment | undefined;
    const hasBreakdowns = !!result?.categoryBreakdowns?.length;

    const psychologicalNeedsSatisfactionCategory = t(
        'traineeReports:individualMotivation.categories.psychologicalNeedsSatisfaction'
    );
    const levelsOfRegulationCategory = t('traineeReports:individualMotivation.categories.levelsOfRegulation');

    const psychologicalNeedsSatisfactionAxes = [
        t('traineeReports:individualMotivation.subcategories.autonomy'),
        t('traineeReports:individualMotivation.subcategories.competence'),
        t('traineeReports:individualMotivation.subcategories.relatedness'),
    ];

    const levelsOfRegulationAxes = [
        t('traineeReports:individualMotivation.subcategories.intrinsicMotivation'),
        t('traineeReports:individualMotivation.subcategories.integratedRegulation'),
        t('traineeReports:individualMotivation.subcategories.identifiedRegulation'),
        t('traineeReports:individualMotivation.subcategories.introjectedRegulation'),
        t('traineeReports:individualMotivation.subcategories.externalRegulation'),
        t('traineeReports:individualMotivation.subcategories.amotivation'),
    ];

    const radarGroups: PsychologicalRadarGroup[] = hasBreakdowns
        ? (result?.categoryBreakdowns || []).map(cb => {
              const domainMax = Math.max(1, ...cb.subcategoryAverages.map(s => s.maximumPossibleScore));
              return {
                  key: cb.category,
                  scoreCards: cb.subcategoryAverages.map(s => ({
                      label: s.subcategory,
                      value: t('traineeReports:na'),
                  })),
                  charts: [
                      {
                          data: cb.subcategoryAverages.map(s => ({
                              axis: s.subcategory,
                              individual: 0,
                          })),
                          maxDomain: domainMax,
                          height: individualMotivationHeight,
                          variant: 'bare',
                          footer: cb.category,
                      },
                  ],
              };
          })
        : [
              // No structure available (e.g., placeholder/incomplete result) -> render stable "NA" charts using default, translated axes.
              {
                  key: 'individual-motivation-psychological-needs-satisfaction',
                  scoreCards: psychologicalNeedsSatisfactionAxes.map(label => ({
                      label,
                      value: t('traineeReports:na'),
                  })),
                  charts: [
                      {
                          data: psychologicalNeedsSatisfactionAxes.map(axis => ({
                              axis,
                              individual: 0,
                          })),
                          maxDomain: 5,
                          height: individualMotivationHeight,
                          variant: 'bare',
                          footer: psychologicalNeedsSatisfactionCategory,
                      },
                  ],
              },
              {
                  key: 'individual-motivation-levels-of-regulation',
                  scoreCards: levelsOfRegulationAxes.map(label => ({
                      label,
                      value: t('traineeReports:na'),
                  })),
                  charts: [
                      {
                          data: levelsOfRegulationAxes.map(axis => ({
                              axis,
                              individual: 0,
                          })),
                          maxDomain: 5,
                          height: individualMotivationHeight,
                          variant: 'bare',
                          footer: levelsOfRegulationCategory,
                      },
                  ],
              },
          ];

    return {
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier,
        name: mi.name ?? '',
        description: mi.description ?? '',
        radarGroups,
    };
};
